import React, { useState, useEffect, useCallback } from 'react';
import AOHeader from '../../components/ao/AOHeader';
import LoadingSpinner from '../../components/operators/LoadingSpinner';

function FirstCommentStatusBadge({ status, errorMessage }) {
  if (!status) return <span className="text-xs text-gray-400">First comment: —</span>;
  const label = `First comment: ${status}`;
  const colors = {
    posted: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    skipped: 'bg-gray-100 text-gray-600',
    unsupported: 'bg-amber-100 text-amber-800',
    pending: 'bg-blue-50 text-blue-700'
  };
  const cls = colors[status] || 'bg-gray-100 text-gray-700';
  return (
    <span title={errorMessage || label} className={`text-xs px-2 py-0.5 rounded ${cls}`}>
      {label}
      {status === 'failed' && errorMessage && (
        <span className="ml-1" title={errorMessage}> (hover for error)</span>
      )}
    </span>
  );
}

/** ISO → value suitable for <input type="datetime-local"> in the browser's local zone. */
function isoToDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function Publishing() {
  const [email, setEmail] = useState('');
  const [authChecked, setAuthChecked] = useState(false);
  const [scheduled, setScheduled] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishLoading, setPublishLoading] = useState(false);
  const [filter, setFilter] = useState('scheduled'); // scheduled | drafts | history
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [patchLoading, setPatchLoading] = useState(false);

  const [draftsLoading, setDraftsLoading] = useState(false);
  const [readyDrafts, setReadyDrafts] = useState([]);
  const [studioDrafts, setStudioDrafts] = useState([]);
  const [draftSchedule, setDraftSchedule] = useState({}); // { [ideaId]: { linkedin, facebook, instagram, x } }
  const [draftEdits, setDraftEdits] = useState({}); // { [ideaId]: { linkedin, facebook, instagram, x } }
  const [studioSchedule, setStudioSchedule] = useState({}); // { [quoteId]: { linkedin, facebook, instagram, x } }
  const [studioEdits, setStudioEdits] = useState({}); // { [quoteId]: { linkedin, facebook, instagram, x } }
  /** Shared analytics-grounded defaults: { linkedin, facebook, instagram, x } as datetime-local strings */
  const [scheduleDefaults, setScheduleDefaults] = useState(null);
  const [scheduleDefaultsNote, setScheduleDefaultsNote] = useState('');
  const [draftError, setDraftError] = useState('');
  const [historyMessage, setHistoryMessage] = useState('');
  const [historyError, setHistoryError] = useState('');

  const handleNavigate = useCallback((path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/ao/me');
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.ok) {
          window.location.replace('/ao/login');
          return;
        }
        if (!cancelled) {
          setEmail(json.email || '');
          setAuthChecked(true);
        }
      } catch (_) {
        window.location.replace('/ao/login');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!authChecked) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/ao/publishing/recommended-schedule');
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.ok || !json.channels) return;
        const map = {};
        const notes = [];
        for (const ch of ['linkedin', 'facebook', 'instagram', 'x']) {
          const info = json.channels[ch];
          if (!info?.scheduled_at) continue;
          map[ch] = isoToDatetimeLocalValue(info.scheduled_at);
          if (info.source === 'engagement_data') {
            notes.push(`${ch}: based on ${info.sample_count || 0} scored posts`);
          } else {
            notes.push(`${ch}: general fallback (not enough engagement history yet)`);
          }
        }
        if (!cancelled) {
          setScheduleDefaults(map);
          setScheduleDefaultsNote(notes.join(' · '));
        }
      } catch (_) {
        /* non-fatal — fields stay empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authChecked]);

  const fetchPosts = useCallback(async () => {
    if (!authChecked) return;
    if (!email) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [schedRes, histRes] = await Promise.all([
        fetch(`/api/ao/scheduled-posts?status=scheduled&limit=50`),
        fetch(`/api/ao/scheduled-posts?limit=50`),
      ]);
      const schedJson = await schedRes.json().catch(() => ({}));
      const histJson = await histRes.json().catch(() => ({}));
      if (schedJson.ok && schedJson.posts) setScheduled(schedJson.posts);
      if (histJson.ok && histJson.posts) {
        const postedOrFailed = (histJson.posts || []).filter((p) => p.status === 'posted' || p.status === 'failed');
        setHistory(postedOrFailed);
      }
    } catch (_) {}
    setLoading(false);
  }, [authChecked, email]);

  const fetchReadyDrafts = useCallback(async () => {
    if (!authChecked) return;
    setDraftsLoading(true);
    setDraftError('');
    try {
      const params = new URLSearchParams();
      params.set('status', 'all');
      params.set('path', 'ready_post');
      params.set('limit', '100');
      params.set('offset', '0');
      const res = await fetch(`/api/ao/ideas?${params.toString()}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || 'Failed to load drafts');
      const rows = (json.ideas || []).filter((x) => x.ready_social_drafts && x.ready_target_social);
      setReadyDrafts(rows);
    } catch (e) {
      setDraftError(e.message);
    } finally {
      setDraftsLoading(false);
    }
  }, [authChecked]);

  const fetchStudioDrafts = useCallback(async () => {
    if (!authChecked) return;
    setDraftsLoading(true);
    setDraftError('');
    try {
      const res = await fetch(`/api/ao/quotes/list?status=approved&limit=200&offset=0`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || 'Failed to load Studio drafts');
      const rows = (json.quotes || []).filter((x) => x && x.status === 'approved' && x.next_stage === 'publisher');
      setStudioDrafts(rows);
    } catch (e) {
      setStudioDrafts([]);
      setDraftError(e.message);
    } finally {
      setDraftsLoading(false);
    }
  }, [authChecked]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handlePublishNow = async () => {
    setPublishLoading(true);
    try {
      const res = await fetch('/api/social/publish-now', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (data.ok) {
        await fetchPosts();
      }
    } catch (_) {}
    setPublishLoading(false);
  };

  const handlePatchFirstComment = useCallback(async (id, first_comment) => {
    if (!authChecked) return;
    setPatchLoading(true);
    try {
      const res = await fetch(`/api/ao/scheduled-posts/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_comment }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.ok) {
        setEditingId(null);
        setEditValue('');
        await fetchPosts();
      }
    } catch (_) {}
    setPatchLoading(false);
  }, [authChecked, fetchPosts]);

  const handleSaveFeedback = useCallback(async (id, feedback_rating, feedback_notes) => {
    if (!authChecked) return;
    setHistoryMessage('');
    setHistoryError('');
    setPatchLoading(true);
    try {
      const res = await fetch(`/api/ao/scheduled-posts/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback_rating, feedback_notes }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'Could not save feedback');
      setHistoryMessage('Feedback saved.');
      await fetchPosts();
    } catch (e) {
      setHistoryError(e.message || 'Could not save feedback');
    } finally {
      setPatchLoading(false);
    }
  }, [authChecked, fetchPosts]);

  const startEdit = (post) => {
    setEditingId(post.id);
    setEditValue(post.first_comment || '');
  };
  const saveEdit = () => {
    if (editingId == null) return;
    handlePatchFirstComment(editingId, editValue.trim() || null);
  };
  const removeFirstComment = (post) => {
    if (!post.id) return;
    handlePatchFirstComment(post.id, null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AOHeader active="publisher" email={email} onNavigate={handleNavigate} />
      <main className="container mx-auto px-4 py-6 md:py-8 max-w-7xl pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] md:pb-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Publisher</h1>
            <p className="text-gray-600">Schedule and publish. This is where finished work goes live.</p>
          </div>
          <button
            type="button"
            onClick={handlePublishNow}
            disabled={publishLoading}
            className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {publishLoading ? 'Publishing…' : 'Publish due posts now'}
          </button>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            type="button"
            onClick={() => setFilter('scheduled')}
            className={`px-4 py-2 rounded-lg font-medium ${filter === 'scheduled' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            Scheduled
          </button>
          <button
            type="button"
            onClick={() => { setFilter('drafts'); fetchReadyDrafts(); fetchStudioDrafts(); }}
            className={`px-4 py-2 rounded-lg font-medium ${filter === 'drafts' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            Drafts
          </button>
          <button
            type="button"
            onClick={() => setFilter('history')}
            className={`px-4 py-2 rounded-lg font-medium ${filter === 'history' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            History
          </button>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : filter === 'drafts' ? (
          <section className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Drafts</h2>
              <p className="text-sm text-gray-600 mt-1">Drafts ready for scheduling (from Studio and from Ready Posts).</p>
              {scheduleDefaultsNote ? (
                <p className="text-xs text-gray-500 mt-2">Suggested times: {scheduleDefaultsNote}</p>
              ) : null}
              {draftError ? <p className="text-sm text-red-600 mt-2">{draftError}</p> : null}
            </div>
            {draftsLoading ? (
              <div className="p-6"><LoadingSpinner message="Loading drafts…" /></div>
            ) : studioDrafts.length === 0 && readyDrafts.length === 0 ? (
              <p className="p-6 text-gray-500">No drafts yet. Approve items to Publisher from Studio, or create a Ready Post in Ideas and generate drafts.</p>
            ) : (
              <div className="divide-y divide-gray-200">
                {studioDrafts.length ? (
                  <div className="p-6 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900">From Studio</h3>
                    <p className="text-xs text-gray-600 mt-1">Approved items routed to Publisher for scheduling.</p>
                  </div>
                ) : null}
                {studioDrafts.map((q) => {
                  const id = q.id;
                  const d = q.drafts_by_channel || {};
                  const sched = { ...(scheduleDefaults || {}), ...(studioSchedule[id] || {}) };
                  const edits = studioEdits[id] || {};
                  const setSched = (channel, v) => setStudioSchedule((p) => ({ ...p, [id]: { ...(p[id] || {}), [channel]: v } }));
                  const setEdit = (channel, v) => setStudioEdits((p) => ({ ...p, [id]: { ...(p[id] || {}), [channel]: v } }));
                  const scheduleNow = async () => {
                    try {
                      const res = await fetch('/api/ao/publishing/schedule-from-quote', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ quote_id: id, schedule: sched, edits }),
                      });
                      const json = await res.json().catch(() => ({}));
                      if (!res.ok || !json.ok) throw new Error(json.error || 'Schedule failed');
                      await fetchPosts();
                      await fetchStudioDrafts();
                      setFilter('scheduled');
                    } catch (e) {
                      setDraftError(e.message);
                    }
                  };

                  return (
                    <div key={id} className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900 truncate">{q.source_title || q.source_name || 'Studio item'}</div>
                          <div className="text-xs text-gray-500 mt-1">{new Date(q.updated_at || q.created_at).toLocaleString()}</div>
                        </div>
                        <button
                          type="button"
                          onClick={scheduleNow}
                          className="px-4 py-2 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700"
                        >
                          Schedule selected
                        </button>
                      </div>

                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        {['linkedin', 'facebook', 'instagram', 'x'].map((c) => (
                          <div key={c} className="border border-gray-200 rounded p-3">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm font-semibold text-gray-900">{c}</div>
                              <input
                                type="datetime-local"
                                value={sched[c] || ''}
                                onChange={(e) => setSched(c, e.target.value)}
                                className="text-xs border border-gray-300 rounded px-2 py-1"
                              />
                            </div>
                            <textarea
                              value={edits[c] ?? d[c] ?? ''}
                              onChange={(e) => setEdit(c, e.target.value)}
                              rows={4}
                              className="mt-2 w-full text-sm border border-gray-300 rounded px-2 py-1"
                              placeholder="Draft text…"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {readyDrafts.length ? (
                  <div className="p-6 border-t border-gray-200 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900">From Ready Posts</h3>
                    <p className="text-xs text-gray-600 mt-1">Ideas that already have channel drafts generated.</p>
                  </div>
                ) : null}

                {readyDrafts.map((idea) => {
                  const id = idea.id;
                  const d = idea.ready_social_drafts?.drafts_by_channel || {};
                  const sched = { ...(scheduleDefaults || {}), ...(draftSchedule[id] || {}) };
                  const edits = draftEdits[id] || {};
                  const setSched = (channel, v) => setDraftSchedule((p) => ({ ...p, [id]: { ...(p[id] || {}), [channel]: v } }));
                  const setEdit = (channel, v) => setDraftEdits((p) => ({ ...p, [id]: { ...(p[id] || {}), [channel]: v } }));
                  const scheduleNow = async () => {
                    try {
                      const res = await fetch('/api/ao/publishing/schedule-from-idea', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ idea_id: id, schedule: sched, edits }),
                      });
                      const json = await res.json().catch(() => ({}));
                      if (!res.ok || !json.ok) throw new Error(json.error || 'Schedule failed');
                      await fetchPosts();
                      setFilter('scheduled');
                    } catch (e) {
                      setDraftError(e.message);
                    }
                  };

                  return (
                    <div key={id} className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900 truncate">{idea.title || 'Ready Post'}</div>
                          <div className="text-xs text-gray-500 mt-1">{new Date(idea.created_at).toLocaleString()}</div>
                        </div>
                        <button
                          type="button"
                          onClick={scheduleNow}
                          className="px-4 py-2 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700"
                        >
                          Schedule selected
                        </button>
                      </div>

                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        {['linkedin', 'facebook', 'instagram', 'x'].map((c) => (
                          <div key={c} className="border border-gray-200 rounded p-3">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm font-semibold text-gray-900">{c}</div>
                              <input
                                type="datetime-local"
                                value={sched[c] || ''}
                                onChange={(e) => setSched(c, e.target.value)}
                                className="text-xs border border-gray-300 rounded px-2 py-1"
                              />
                            </div>
                            <textarea
                              value={edits[c] ?? d[c] ?? ''}
                              onChange={(e) => setEdit(c, e.target.value)}
                              rows={4}
                              className="mt-2 w-full text-sm border border-gray-300 rounded px-2 py-1"
                              placeholder="Draft text…"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        ) : filter === 'scheduled' ? (
          <section className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <h2 className="sr-only">Scheduled posts</h2>
            {scheduled.length === 0 ? (
              <p className="p-6 text-gray-500">No scheduled posts.</p>
            ) : (
              <ul className="divide-y divide-gray-200">
                {scheduled.map((p) => (
                  <li key={p.id} className="p-4 flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-gray-900">{p.platform} / {p.account_id}</span>
                      <span className="text-gray-500 text-sm ml-2">{new Date(p.scheduled_at).toLocaleString()}</span>
                      <p className="text-sm text-gray-600 mt-1 truncate max-w-xl">{p.text}</p>
                      {(p.why_it_matters || p.ao_lane || p.best_move || p.source_kind) ? (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-xs text-gray-600 hover:text-gray-900">Intent</summary>
                          <div className="mt-2 text-xs text-gray-700 space-y-1">
                            {p.source_kind ? <div><span className="text-gray-500">From:</span> {p.source_kind}</div> : null}
                            {p.ao_lane ? <div><span className="text-gray-500">Lane:</span> {p.ao_lane}</div> : null}
                            {p.best_move ? <div><span className="text-gray-500">Move:</span> {p.best_move}</div> : null}
                            {p.why_it_matters ? <div className="whitespace-pre-wrap"><span className="text-gray-500">Why:</span> {p.why_it_matters}</div> : null}
                          </div>
                        </details>
                      ) : null}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <FirstCommentStatusBadge status={p.first_comment_status || (p.first_comment ? 'pending' : null)} errorMessage={p.first_comment_error_message} />
                        {p.first_comment && !editingId && (
                          <span className="text-xs text-gray-500 max-w-md truncate" title={p.first_comment}>{p.first_comment}</span>
                        )}
                      </div>
                      {editingId === p.id ? (
                        <div className="mt-2">
                          <label className="sr-only">Edit first comment</label>
                          <textarea
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder="First comment (optional)"
                            rows={2}
                            className="mt-1 block w-full max-w-xl rounded border border-gray-300 px-2 py-1 text-sm"
                          />
                          <div className="mt-1 flex gap-2">
                            <button type="button" onClick={saveEdit} disabled={patchLoading} className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">Save</button>
                            <button type="button" onClick={() => { setEditingId(null); setEditValue(''); }} className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-1 flex gap-2">
                          <button type="button" onClick={() => startEdit(p)} className="text-xs text-blue-600 hover:underline">Edit first comment</button>
                          {p.first_comment && (
                            <button type="button" onClick={() => removeFirstComment(p)} disabled={patchLoading} className="text-xs text-red-600 hover:underline disabled:opacity-50">Remove first comment</button>
                          )}
                        </div>
                      )}
                    </div>
                    <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">{p.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : (
          <section className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <h2 className="sr-only">Publishing history</h2>
            {historyMessage ? <p className="p-4 text-sm text-green-700">{historyMessage}</p> : null}
            {historyError ? <p className="p-4 text-sm text-red-700">{historyError}</p> : null}
            {history.length === 0 ? (
              <p className="p-6 text-gray-500">No history yet.</p>
            ) : (
              <ul className="divide-y divide-gray-200">
                {history.map((p) => (
                  <li key={p.id} className="p-4 flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-gray-900">{p.platform} / {p.account_id}</span>
                      <span className="text-gray-500 text-sm ml-2">{new Date(p.updated_at || p.scheduled_at).toLocaleString()}</span>
                      <p className="text-sm text-gray-600 mt-1 truncate max-w-xl">{p.text}</p>
                      {(p.why_it_matters || p.ao_lane || p.best_move || p.source_kind) ? (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-xs text-gray-600 hover:text-gray-900">Intent</summary>
                          <div className="mt-2 text-xs text-gray-700 space-y-1">
                            {p.source_kind ? <div><span className="text-gray-500">From:</span> {p.source_kind}</div> : null}
                            {p.ao_lane ? <div><span className="text-gray-500">Lane:</span> {p.ao_lane}</div> : null}
                            {p.best_move ? <div><span className="text-gray-500">Move:</span> {p.best_move}</div> : null}
                            {p.why_it_matters ? <div className="whitespace-pre-wrap"><span className="text-gray-500">Why:</span> {p.why_it_matters}</div> : null}
                          </div>
                        </details>
                      ) : null}
                      <div className="mt-2">
                        <FirstCommentStatusBadge status={p.first_comment_status} errorMessage={p.first_comment_error_message} />
                        {p.first_comment_status === 'failed' && p.first_comment_error_message && (
                          <p className="text-xs text-red-600 mt-1 max-w-xl">{p.first_comment_error_message}</p>
                        )}
                      </div>
                      {p.status === 'failed' && p.error_message && (
                        <p className="text-sm text-red-600 mt-1">{p.error_message}</p>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {p.feedback_rating ? (
                          <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">Feedback: {p.feedback_rating}{p.feedback_notes ? ` — ${p.feedback_notes}` : ''}</span>
                        ) : (
                          <button
                            type="button"
                            disabled={patchLoading}
                            onClick={() => {
                              const rating = window.prompt('Feedback rating (good / meh / bad)', 'good');
                              if (rating == null) return;
                              const notes = window.prompt('Optional notes (what worked / what didn’t)', '');
                              handleSaveFeedback(p.id, rating, notes || '');
                            }}
                            className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                          >
                            Add feedback
                          </button>
                        )}
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${p.status === 'posted' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{p.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section className="mt-8 bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Platform status</h2>
          <p className="text-gray-500 text-sm">Configured platforms are set via environment variables. See notes/SOCIAL_VERCEL_ENV.md.</p>
          <p className="text-gray-600 text-sm mt-2">
            <strong>First comment supported:</strong> Facebook Page, Instagram, LinkedIn, X. You can add, edit, or remove an optional first comment on each scheduled post above; it is published after the main post (or as a reply on X). Comment status is tracked separately (pending, posted, failed, skipped).
          </p>
        </section>

        <section className="mt-8 bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Media / assets</h2>
          <p className="text-gray-500 text-sm">Quote card images and uploads will appear here when the feature is connected.</p>
        </section>
      </main>
    </div>
  );
}
